import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Gift, Copy, Mail, MessageCircle, Settings } from "lucide-react";

export default function StepConfirmation({ confirmation, email, onHome }) {
  const [copied, setCopied] = useState(false);
  const refCode = confirmation?.referral_code;
  const credit = confirmation?.referral_credit || 25;

  const shareLink = refCode
    ? `${window.location.origin}/book?ref=${refCode}`
    : window.location.origin;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.warn("Clipboard write failed", e);
    }
  };

  const smsBody = encodeURIComponent(
    `I just booked Full Indoor Air for a duct cleaning — use my code ${refCode} for $${credit} off your first service: ${shareLink}`
  );
  const emailSubject = encodeURIComponent("$25 off Full Indoor Air for you");
  const emailBody = encodeURIComponent(
    `Hey — I just had Full Indoor Air clean my home's air ducts. Use my code ${refCode} to get $${credit} off your first booking. They serve MD, DC, and VA: ${shareLink}`
  );

  return (
    <div className="text-center py-12" data-testid="step-confirmation">
      <div className="inline-flex w-16 h-16 rounded-full bg-forest text-white items-center justify-center mb-6">
        <CheckCircle2 size={28} />
      </div>
      <h2 className="font-heading text-4xl tracking-tighter font-light">You're all set.</h2>
      <p className="text-muted2 mt-4 max-w-md mx-auto">
        Your booking is confirmed. We've sent details to <span className="text-ink">{email}</span>.
        A team member will confirm by phone shortly.
      </p>
      <div className="mt-8 inline-flex items-center gap-3 card-surface px-6 py-4">
        <div className="eyebrow">Confirmation #</div>
        <div className="font-heading text-xl tracking-widest" data-testid="confirmation-number">
          {confirmation.confirmation_number}
        </div>
      </div>

      {/* Referral share card */}
      {refCode && (
        <div className="max-w-xl mx-auto mt-10 card-surface p-8 text-left" data-testid="referral-share-card">
          <div className="flex items-center gap-2">
            <Gift size={18} className="text-clay" />
            <span className="eyebrow">Refer a neighbor · earn ${credit}</span>
          </div>
          <h3 className="font-heading text-2xl tracking-tight mt-3">
            Share your code. You both save ${credit}.
          </h3>
          <p className="text-sm text-muted2 mt-2">
            When a neighbor books with your code, they get ${credit} off — and you get ${credit} credit on your next service.
          </p>

          <div className="mt-5 flex items-center gap-3 bg-bone border border-black/10 rounded-2xl p-3">
            <div className="flex-1 px-3">
              <div className="eyebrow">Your code</div>
              <div className="font-heading text-2xl tracking-[0.32em]" data-testid="user-referral-code">{refCode}</div>
            </div>
            <button onClick={copyLink} className="btn-primary text-sm" data-testid="copy-referral-link">
              <Copy size={14} /> {copied ? "Copied!" : "Copy link"}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <a
              href={`sms:?&body=${smsBody}`}
              className="btn-ghost text-sm"
              data-testid="share-sms"
            >
              <MessageCircle size={14} /> Text it
            </a>
            <a
              href={`mailto:?subject=${emailSubject}&body=${emailBody}`}
              className="btn-ghost text-sm"
              data-testid="share-email"
            >
              <Mail size={14} /> Email it
            </a>
          </div>
        </div>
      )}

      <div className="mt-10 flex gap-3 justify-center flex-wrap">
        <Link
          to={`/my-booking?conf=${confirmation.confirmation_number}&email=${encodeURIComponent(email)}`}
          className="btn-ghost"
          data-testid="confirmation-manage-link"
        >
          <Settings size={14} /> Manage my booking
        </Link>
        <button onClick={onHome} className="btn-ghost" data-testid="back-home">Back to home</button>
      </div>
    </div>
  );
}
